<template>
  <div>
    <div class="flex flex-col items-start w-full h-full px-6 pt-6 pb-16 sm:px-12 text-[#182233] dark:text-slate-100">
      <section class="w-full rounded-[28px] bg-[linear-gradient(135deg,#F6FAFF_0%,#EEF4FF_45%,#F7FBF8_100%)] dark:bg-[linear-gradient(135deg,#162235_0%,#111b2c_45%,#132b27_100%)] p-6 shadow-[0_24px_80px_rgba(57,64,79,0.08)] dark:shadow-none sm:p-8">
      <div class="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
        <div class="max-w-2xl">
          <p class="text-sm font-semibold uppercase tracking-[0.24em] text-[#6D86B0] dark:text-slate-400">Number Workspace</p>
          <h2 class="mt-3 text-3xl font-bold text-[#182233] dark:text-slate-100 font-manrope">Manage your numbers</h2>
          <p class="mt-3 max-w-xl text-sm leading-6 text-[#5F6E82] dark:text-slate-300 font-manrope sm:text-base">
            Reserve, subscribe, and organize owned or invited numbers from one place. Regulated numbers keep registration guidance visible throughout the flow.
          </p>
        </div>

        <div class="relative flex flex-col items-end gap-1 group">
          <Button
            variant="primary"
            title="Order a Number"
            :size="'md'"
            class="rounded-[12px] px-8 py-3"
            :disabled="reservedNumbers.length > 0"
            @click="openChooseServiceSheet"
          />
          <div
            v-if="reservedNumbers.length > 0"
            class="pointer-events-none absolute bottom-full mb-2 right-0 hidden group-hover:block w-64 rounded-lg bg-gray-900 dark:bg-slate-700 px-3 py-2 text-xs text-white shadow-lg"
          >
            Cancel your pending reservation before ordering a new number.
            <span class="absolute top-full right-4 border-4 border-transparent border-t-gray-900 dark:border-t-slate-700" />
          </div>
          <p
            v-if="reservedNumbers.length > 0"
            class="w-64 text-xs text-right text-[#5F6E82] dark:text-slate-300"
          >
            Cancel your pending reservation before ordering a new number.
          </p>
        </div>
      </div>

      <div class="mt-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <div class="rounded-[20px] bg-white dark:bg-slate-900/60 p-4 backdrop-blur-sm border border-transparent dark:border-slate-700">
          <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#7A8798] dark:text-slate-400">Reserved</p>
          <p class="mt-2 text-3xl font-bold text-[#182233] dark:text-slate-100 font-manrope">{{ numbersStats.reserved }}</p>
        </div>
        <div class="rounded-[20px] bg-white dark:bg-slate-900/60 p-4 backdrop-blur-sm border border-transparent dark:border-slate-700">
          <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#7A8798] dark:text-slate-400">Owned</p>
          <p class="mt-2 text-3xl font-bold text-[#1D6B4F] font-manrope">{{ numbersStats.owned }}</p>
        </div>
        <div class="rounded-[20px] bg-white dark:bg-slate-900/60 p-4 backdrop-blur-sm border border-transparent dark:border-slate-700">
          <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#7A8798] dark:text-slate-400">Invited</p>
          <p class="mt-2 text-3xl font-bold text-[#304D8A] font-manrope">{{ numbersStats.invited }}</p>
        </div>
        <div class="rounded-[20px] bg-white dark:bg-slate-900/60 p-4 backdrop-blur-sm border border-transparent dark:border-slate-700">
          <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#7A8798] dark:text-slate-400">Need Registration</p>
          <p class="mt-2 text-3xl font-bold text-[#8C6400] font-manrope">{{ numbersStats.registrationRequired }}</p>
        </div>
      </div>
    </section>

    <div v-if="isLoadingRef" class="flex items-center justify-center min-h-72 w-full">
      <Spinner />
    </div>

    <template v-else>
      <section v-if="reservedNumbers.length" class="w-full mt-8 rounded-[24px] border border-[#E3ECFF] dark:border-slate-700 bg-white dark:bg-slate-900 p-5 shadow-[0_18px_60px_rgba(57,64,79,0.08)] dark:shadow-none sm:p-6">
        <div class="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 class="text-xl font-bold text-[#182233] dark:text-slate-100 font-manrope">Reserved numbers</h3>
            <p class="mt-1 text-sm text-[#5F6E82] dark:text-slate-300 font-manrope">Finish registration and checkout before each reservation expires.</p>
          </div>
          <p class="text-sm text-[#5F6E82] dark:text-slate-300 font-manrope">{{ reservedNumbers.length }} {{ reservedNumbers.length === 1 ? 'reservation' : 'reservations' }}</p>
        </div>

        <ul class="mt-6 space-y-4">
        <ReservedNumberListItem
          v-for="number in reservedNumbers"
          :key="number.did"
          :number="number"
          :registration-required="number.needsRegistration"
          :registration="selectedNumberRegistration"
          :is-processing-order="isLoadingOrder"
          @cancel="confirmCancelReservation(number)"
          @attach-registration="fetchAndOpenModalNumbersRegistration(number)"
          @detach-registration="removeRegistrationFromNumber"
          @start-subscribe="subscribeToNumber"
        />
        </ul>
      </section>

      <div class="w-full mt-8 rounded-[16px] bg-[#F4F7FE] dark:bg-slate-800 p-1 flex">
        <button
          type="button"
          class="flex-1 rounded-lg px-4 py-3 text-left"
          :class="activeTab === 'your' ? 'bg-white dark:bg-slate-900 text-blue-500 shadow-sm' : 'text-[#5F6E82] dark:text-slate-300'"
          @click="activeTab = 'your'"
        >
          <p class="text-lg font-medium font-manrope">Your Numbers</p>
          <p class="text-sm font-manrope">Own these numbers</p>
        </button>
        <button
          type="button"
          class="flex-1 rounded-lg px-4 py-3 text-left"
          :class="activeTab === 'invite' ? 'bg-white dark:bg-slate-900 text-blue-500 shadow-sm' : 'text-[#5F6E82] dark:text-slate-300'"
          @click="activeTab = 'invite'"
        >
          <p class="text-lg font-medium font-manrope">Invite Numbers</p>
          <p class="text-sm font-manrope">Destination on these numbers</p>
        </button>
      </div>

      <section class="w-full mt-8 rounded-[24px] border border-[#E3ECFF] dark:border-slate-700 bg-white dark:bg-slate-900 p-5 shadow-[0_18px_60px_rgba(57,64,79,0.08)] dark:shadow-none sm:p-6">
        <div class="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 class="text-xl font-bold text-[#182233] dark:text-slate-100 font-manrope">{{ activeTab === 'your' ? 'Active numbers' : 'Invited numbers' }}</h3>
            <p class="mt-1 text-sm text-[#5F6E82] dark:text-slate-300 font-manrope">
              {{ activeTab === 'your' ? 'Numbers already assigned to your workspace.' : 'Numbers where you are a destination but not the owner.' }}
            </p>
          </div>
          <p class="text-sm text-[#5F6E82] dark:text-slate-300 font-manrope">
            {{ activeTab === 'your' ? ownedAssignedNumbers.length : invitedAssignedNumbers.length }}
            {{ activeTab === 'your'
              ? (ownedAssignedNumbers.length === 1 ? 'owned number' : 'owned numbers')
              : (invitedAssignedNumbers.length === 1 ? 'invite' : 'invites') }}
          </p>
        </div>

        <div v-if="activeTab === 'your' && ownedAssignedNumbers.length === 0" class="mt-6 rounded-[20px] border border-dashed border-[#D3DCEC] dark:border-slate-700 bg-[#FAFCFF] dark:bg-slate-800 p-10 text-center">
          <h4 class="text-xl font-bold text-[#182233] dark:text-slate-100 font-manrope">No active owned numbers</h4>
          <p class="mx-auto mt-3 max-w-lg text-sm leading-6 text-[#5F6E82] dark:text-slate-300 font-manrope">
            You don't have any Beesphone numbers yet. Order your first number to start making and receiving calls.
          </p>
          <Button
            variant="primary"
            title="Order a Beesphone Number"
            size="md"
            class="mt-6 rounded-[12px] px-8 py-3"
            :disabled="reservedNumbers.length > 0"
            @click="openChooseServiceSheet"
          />
        </div>

        <div v-if="activeTab === 'invite' && invitedAssignedNumbers.length === 0" class="mt-6 rounded-[20px] border border-dashed border-[#D3DCEC] dark:border-slate-700 bg-[#FAFCFF] dark:bg-slate-800 p-10 text-center">
          <h4 class="text-xl font-bold text-[#182233] dark:text-slate-100 font-manrope">No invited numbers</h4>
          <p class="mx-auto mt-3 max-w-lg text-sm leading-6 text-[#5F6E82] dark:text-slate-300 font-manrope">
            Shared destinations will appear here once another workspace adds you to one of its numbers.
          </p>
        </div>

        <ul v-if="activeTab === 'your' && ownedAssignedNumbers.length" class="mt-6 grid gap-4">
          <li v-for="number in ownedAssignedNumbers" :key="number._id" class="rounded-[22px] border border-[#E3ECFF] dark:border-slate-700 bg-[#FBFDFF] dark:bg-slate-800 p-5">
            <div class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div class="flex items-start gap-4">
                <div class="flex size-12 items-center justify-center rounded-[16px] bg-[#E7F0FF] dark:bg-slate-700 text-sm font-bold text-[#304D8A] dark:text-slate-100 font-manrope">
                  {{ number.did.slice(0, 2) }}
                </div>
                <div>
                  <div class="flex flex-wrap items-center gap-2">
                    <h6 class="font-semibold text-[#182233] dark:text-slate-100 font-manrope text-lg">{{ number.did }}</h6>
                    <span v-if="number.didTitle" class="rounded-full bg-[#EEF2F7] dark:bg-slate-700 px-2.5 py-1 text-xs font-semibold text-[#5F6E82] dark:text-slate-200 font-manrope">
                      {{ number.didTitle }}
                    </span>
                  </div>
                </div>
              </div>

              <Button
                variant="secondary"
                title="Manage"
                size="md"
                class="rounded-[12px] px-6 py-3"
                @click="openManageInAppModal(number)"
              />
            </div>

            <!-- Destination toggles — allow enabling/disabling routing individually -->
            <div v-if="number.destinations?.length" class="mt-4 border-t border-[#E3ECFF] dark:border-slate-700 pt-4 space-y-2">
              <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#7A8798] dark:text-slate-400 font-manrope mb-3">Destinations</p>
              <ul class="space-y-2">
                <li
                  v-for="dest in number.destinations"
                  :key="getDestinationKey(dest)"
                  class="flex items-center justify-between rounded-[14px] border border-[#E3ECFF] dark:border-slate-700 bg-white dark:bg-slate-900 px-4 py-3 transition-opacity"
                  :class="dest.isEnabled === false ? 'opacity-50' : ''"
                >
                  <div class="min-w-0 flex-1 pr-4">
                    <p class="text-sm font-semibold text-[#182233] dark:text-slate-100 font-manrope truncate">{{ dest.title }}</p>
                    <p
                      v-if="dest.number && dest.title !== dest.number"
                      class="mt-0.5 text-xs text-[#5F6E82] dark:text-slate-300 font-manrope truncate"
                    >
                      {{ dest.number }}
                    </p>
                    <!-- Pending invite badge -->
                    <p
                      v-if="dest.status === 'pending' || dest.status === 'rejected'"
                      class="mt-0.5 text-xs text-amber-600 dark:text-amber-400 font-manrope"
                    >
                      {{ dest.status === 'rejected' ? 'Invite declined' : 'Waiting for invite response' }}
                    </p>
                  </div>
                  <!-- Toggle disabled until the destination owner responds to the invite -->
                  <button
                    type="button"
                    role="switch"
                    :aria-checked="dest.isEnabled !== false"
                    :aria-label="`${dest.isEnabled !== false ? 'Disable' : 'Enable'} ${dest.title}`"
                    :disabled="dest.status === 'pending' || dest.status === 'rejected'"
                    class="relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-slate-900"
                    :class="[
                      dest.status === 'pending' || dest.status === 'rejected'
                        ? 'cursor-not-allowed opacity-40'
                        : 'cursor-pointer',
                      dest.isEnabled !== false ? 'bg-blue-500' : 'bg-gray-200 dark:bg-slate-600',
                    ]"
                    @click="dest.status !== 'pending' && dest.status !== 'rejected' && toggleDestination(number.did, dest)"
                  >
                    <span
                      class="pointer-events-none inline-block size-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
                      :class="dest.isEnabled !== false ? 'translate-x-5' : 'translate-x-0'"
                    />
                  </button>
                </li>
              </ul>
            </div>
          </li>
        </ul>

        <ul v-if="activeTab === 'invite' && invitedAssignedNumbers.length" class="mt-6 grid gap-4">
          <li v-for="number in invitedAssignedNumbers" :key="number._id" class="rounded-[22px] border border-[#E3ECFF] dark:border-slate-700 bg-[#FBFDFF] dark:bg-slate-800 p-5">
            <div class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div class="flex items-start gap-4">
                <div class="flex size-12 items-center justify-center rounded-[16px] bg-[#F0F5FF] dark:bg-slate-700 text-sm font-bold text-[#304D8A] dark:text-slate-100 font-manrope">
                  {{ number.did.slice(0, 2) }}
                </div>
                <div>
                  <h6 class="font-semibold text-[#182233] dark:text-slate-100 font-manrope text-lg">{{ number.ownerName || 'Number Owner' }}</h6>
                  <p class="mt-1 text-[#182233] dark:text-slate-100 font-manrope">{{ number.did }}</p>
                  <p v-if="number.joinedAt" class="mt-2 text-sm text-[#5F6E82] dark:text-slate-300 font-manrope">
                    Joined {{ formatJoinedDate(number.joinedAt) }}
                  </p>
                </div>
              </div>

              <button
                type="button"
                class="text-sm font-semibold text-red-600 font-manrope hover:opacity-75"
                @click="confirmLeaveInvitedNumber(number)"
              >
                Leave
              </button>
            </div>
          </li>
        </ul>
      </section>
    </template>
    </div>

    <teleport to="body">
      <transition name="sheet-fade">
        <div
          v-if="showChooseServiceSheet"
          class="fixed inset-0 z-40 bg-black/50"
          @click="showChooseServiceSheet = false"
        />
      </transition>
      <transition name="sheet-slide">
        <div
          v-if="showChooseServiceSheet"
          class="fixed bottom-0 left-0 right-0 z-50 w-full"
        >
          <div class="mx-auto w-full max-w-3xl rounded-t-2xl bg-white dark:bg-slate-900 p-5 sm:p-6 border border-transparent dark:border-slate-700">
            <div class="mx-auto mb-4 h-1.5 w-12 rounded-full bg-gray-300" />

            <h3 class="font-manrope text-2xl font-bold text-[#111827] dark:text-slate-100">Add a number</h3>

            <button
              type="button"
              class="mt-4 w-full overflow-hidden rounded-2xl border text-left"
              :class="selectedServiceType === 'phone' ? 'border-blue-500 ring-2 ring-blue-100 dark:ring-blue-900/40' : 'border-gray-200 dark:border-slate-700'"
              @click="selectedServiceType = 'phone'"
            >
              <img src="/images/fram2.png" alt="Phone System" class="h-40 w-full object-cover sm:h-48" >
              <div class="p-4">
                <p class="font-manrope text-lg font-semibold text-[#111827] dark:text-slate-100">Phone System</p>
                <p class="mt-1 font-manrope text-sm text-[#4B5563] dark:text-slate-300">
                  All-in-one business communication with AI-powered calls, messaging, and collaboration.
                </p>
              </div>
            </button>

            <button
              type="button"
              class="mt-3 w-full overflow-hidden rounded-2xl border text-left"
              :class="selectedServiceType === 'conference' ? 'border-blue-500 ring-2 ring-blue-100 dark:ring-blue-900/40' : 'border-gray-200 dark:border-slate-700'"
              @click="selectedServiceType = 'conference'"
            >
              <img src="/images/fram1.png" alt="Conference Calls" class="h-40 w-full object-cover sm:h-48" >
              <div class="p-4">
                <p class="font-manrope text-lg font-semibold text-[#111827] dark:text-slate-100">Conference Calls</p>
                <p class="mt-1 font-manrope text-sm text-[#4B5563] dark:text-slate-300">
                  Host HD call and video conferences with smart moderator features.
                </p>
              </div>
            </button>

            <div class="mt-4 flex items-start gap-2">
              <span class="text-blue-500">&#9888;</span>
              <p class="font-manrope text-sm text-[#4B5563] dark:text-slate-300">
                Due to local laws, some numbers require identity and/or address verification before activation.
              </p>
            </div>

            <div class="mt-5">
              <Button
                variant="primary"
                title="Continue"
                size="md"
                class="w-full rounded-md py-3"
                @click="continueWithSelectedService"
              />
            </div>
          </div>
        </div>
      </transition>
    </teleport>

    <Modal :show="showSelectRegistrationModal" @close="showSelectRegistrationModal = false">
      <h3 class="text-[#182233] dark:text-slate-100 text-lg font-manrope font-bold">
        Select Registration
        <small class="ml-1 text-sm font-normal text-slate-700 dark:text-slate-300">{{ countryIsoOfSelectedNumber }}</small>
      </h3>

    <ul v-if="!isLoadingNumberRegistrations && numberRegistrations.length" class="flex flex-col flex-1 pt-4 overflow-y-auto number-reg-list">
      <li v-for="reg in numberRegistrations" :key="reg._id" class="flex items-center justify-between p-3 mb-2 border border-slate-200 dark:border-slate-700 rounded-md">
        <label class="flex items-center flex-1 cursor-pointer">
          <RadioInput v-model="selected" name="radio" class="mr-3" :value="reg._id" />
          <h6 class="font-semibold text-[#182233] dark:text-slate-100 font-manrope truncate mr-2" :title="identityName(reg.identityType, reg.identity)">
            {{  identityName(reg.identityType, reg.identity) }}
          </h6>
          <AlertIcon
            v-if="!reg.isVerified"
            class="w-3 h-auto m1-2 min-w-4"
            stroke-class="text-red-500"
            tooltip-text="Not Verified"
          />
        </label>

        <span class="text-sm font-medium text-slate-700 dark:text-slate-300 font-manrope mx-4 capitalize">{{ reg.identityType }}</span>
      </li>
    </ul>
    <div v-else-if="!isLoadingNumberRegistrations && !numberRegistrations.length" class="flex items-center justify-center h-40">
      <p class="text-center text-gray-500 dark:text-slate-400">You do not have a number registration profile for the region of the number which you have reserved.</p>
    </div>
    <div v-else class="flex items-center justify-center h-40">
      <Spinner />
    </div>

      <div class="flex mt-8" :class="!isLoadingNumberRegistrations && numberRegistrations.length ? 'justify-between' : 'justify-start'">
        <Button
          type="button"
          variant="secondary"
          title="Add New Registration"
          size="md"
          class="rounded-[6px] px-6 py-2.5"
          @click="$router.push('/number-registrations/new')"
        />
        <Button
          v-if="!isLoadingNumberRegistrations && numberRegistrations.length"
          variant="primary"
          title="Assign"
          size="md"
          class="rounded-[6px] px-6 py-2.5"
          :disabled="!selected || !numberRegistrations.find(r => r._id === selected)?.isVerified"
          @click="handleAssignRegistration"
        />
      </div>
    </Modal>

  <!-- Confirmation Modal -->
    <Modal :show="showCancelConfirmationModal" :size="'small'" :hide-close="true" @close="showCancelConfirmationModal = false">
      <h4 class="text-[#182233] dark:text-slate-100 text-lg font-manrope font-bold mt-4">
        Are you sure you want to cancel the reservation?
      </h4>
      <div class="flex justify-end mt-8">
        <Button
          type="button"
          variant="secondary"
          title="Cancel"
          size="md"
          class="rounded-[6px] px-6 py-2.5"
          @click="showCancelConfirmationModal = false"
        />
        <Button
          type="button"
          variant="primary"
          title="Yes"
          size="md"
          class="rounded-[6px] px-6 py-2.5 ml-4"
          @click="handleCancelReservation"
        />
      </div>
    </Modal>

  <!-- Stripe pricing modal -->
    <Modal :show="showStripePricingModal" :size="'small'" force-white @close="showStripePricingModal = false">
      <h4 class="text-[#182233] text-lg font-manrope font-bold mt-4">
        Subscribe
      </h4>
      <StripePricingTable 
      :pricing-table-id="stripePricingTableId" 
      :publishable-key="stripePublicKey"
      :client-reference-id="orderId"
      :customer-session-client-secret="customerSessionClientSecret"
      />
    </Modal>

    <Modal :show="showLeaveConfirmationModal" :size="'small'" @close="showLeaveConfirmationModal = false">
      <h4 class="text-[#182233] text-lg font-manrope font-bold mt-4">
        Leave number?
      </h4>
      <p class="mt-4 text-[#374151] font-manrope">
        Are you sure you want to leave being a destination on {{ invitedNumberToLeave?.did }}?
      </p>
      <div class="flex justify-end mt-8">
        <Button
          type="button"
          variant="secondary"
          title="Cancel"
          size="md"
          class="rounded-[6px] px-6 py-2.5"
          @click="showLeaveConfirmationModal = false"
        />
        <button
          type="button"
          class="ml-4 px-6 py-2.5 text-red-600 font-manrope"
          @click="handleLeaveInvitedNumber"
        >
          Leave
        </button>
      </div>
    </Modal>

    <Modal :show="showManageInAppModal" :size="'small'" @close="showManageInAppModal = false">
      <div class="px-2 py-2">
        <div class="mx-auto flex size-16 items-center justify-center rounded-[20px] bg-[linear-gradient(135deg,#E8F1FF_0%,#E9FFF5_100%)] text-[#304D8A] dark:bg-[linear-gradient(135deg,#1E3A5F_0%,#15352F_100%)] dark:text-slate-100">
          <svg class="size-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M12 18h.01M8.5 3h7A1.5 1.5 0 0117 4.5v15a1.5 1.5 0 01-1.5 1.5h-7A1.5 1.5 0 017 19.5v-15A1.5 1.5 0 018.5 3z" />
          </svg>
        </div>

        <h4 class="mt-5 text-center text-xl font-manrope font-bold text-[#182233] dark:text-slate-100">
          Manage this number in the app
        </h4>

        <p class="mx-auto mt-3 max-w-sm text-center text-sm leading-6 text-[#5F6E82] dark:text-slate-300 font-manrope">
          Number management is currently available in the Beesphone mobile app. Open the app to update routing, destinations, and other settings for
          <span class="font-semibold text-[#182233] dark:text-slate-100">{{ numberToManage?.did || 'this number' }}</span>.
        </p>

        <div class="mt-6 rounded-[18px] bg-[#F6FAFF] dark:bg-slate-800 px-4 py-3 text-sm text-[#5F6E82] dark:text-slate-300 font-manrope">
          Tip: if you already have the app installed, open it and go to <span class="font-semibold text-[#182233] dark:text-slate-100">Numbers</span> to continue.
        </div>

        <div class="mt-8 flex justify-end">
          <Button
            type="button"
            variant="primary"
            title="Got it"
            size="md"
            class="rounded-[10px] px-6 py-2.5"
            @click="showManageInAppModal = false"
          />
        </div>
      </div>
    </Modal>
  </div>
</template>

<script setup lang="ts">
import Button from "~/components/common/Button.vue";
import Spinner from "~/components/common/Spinner.vue";
import RadioInput from "~/components/common/RadioInput.vue";
import ReservedNumberListItem from "~/components/common/ReservedNumberListItem.vue";
import AlertIcon from "~/components/icons/AlertIcon.vue";
import StripePricingTable from "~/components/StripePricingTable.vue";
import Modal from "~/components/Modal.vue";
import {
  getBpNumbersList,
  getReservedBpNumbers,
  unReserveBpNumber,
  getNumberGroup,
  unlinkInvitedNumber,
  toggleDestinationEnabled,
} from "@/services/numbers";
import { getNumberRegistrationListByCountry } from "@/services/numberRegistration";
import { useNavbarInfo } from "~/stores/useNabvarInfo";
import type {
  BusinessIdentity,
  PersonalIdentity,
  NumberRegistration,
} from "@/types/numberRegistration";
import type { Country, Destination } from "@/types/numbers";
import {
  getStripeCustomerSessionClientSecret,
  startBeesphoneSoloWithNumberSubscription,
} from "@/services/billing";

type AssignedNumber = {
  _id:          string;
  did:          string;
  didTitle:     string;
  ownerName?:   string;
  invitedTo?:   boolean;
  joinedAt?:    string;
  destinations?: Destination[];
}

type ReservedNumber = {
  _id:         string;
  did:         string;
  isExternal:  boolean | null;
  reservation: Reservation;
  groupId:     string;
  country:   Country;
  needsRegistration: boolean;
  numberRegistrationId?: string
}

type Reservation = {
    endAt:   string;
    startAt: string;
}

const router = useRouter();

const runtimeConfig = useRuntimeConfig();

const reservedNumbers = ref([]) as Ref<ReservedNumber[]>;
const assignedNumbers = ref([]) as Ref<AssignedNumber[]>;
const isLoadingRef = ref(false);
const activeTab = ref<"your" | "invite">("your");
const showChooseServiceSheet = ref(false);
const selectedServiceType = ref<"phone" | "conference">("phone");

const ownedAssignedNumbers = computed(() => assignedNumbers.value.filter((number) => number.invitedTo !== true));
const invitedAssignedNumbers = computed(() => assignedNumbers.value.filter((number) => number.invitedTo === true));

const numbersStats = computed(() => {
  return {
    reserved: reservedNumbers.value.length,
    owned: ownedAssignedNumbers.value.length,
    invited: invitedAssignedNumbers.value.length,
    registrationRequired: reservedNumbers.value.filter((number) => number.needsRegistration).length,
  };
});

// cancel reservation refs
const showCancelConfirmationModal = ref(false);
const numberToCancel = ref<ReservedNumber | null>(null);

// number registration refs
const isLoadingNumberRegistrations = ref(false);
const selectedReservedNumberDid = ref("");
const selectedNumberRegistration = ref<NumberRegistration | undefined>(undefined);
const numberRegistrations = ref([]) as Ref<NumberRegistration[]>;
const countryIsoOfSelectedNumber = ref("");
const selected = ref("");
const showSelectRegistrationModal = ref(false);
const showStripePricingModal = ref(false);
const showLeaveConfirmationModal = ref(false);
const invitedNumberToLeave = ref<AssignedNumber | null>(null);
const showManageInAppModal = ref(false);
const numberToManage = ref<AssignedNumber | null>(null);

// stripe pricing table
const stripePricingTableId = runtimeConfig.public.stripeBeesphoneSoloPlanPricingTableId;
const stripePublicKey = runtimeConfig.public.stripePublicKey;
const orderId = ref("");
const customerSessionClientSecret = ref("");

// order
const isLoadingOrder = ref(false);

definePageMeta({
  layout: "default",
});

useHead({
  title: "My Numbers",
});

const navbarStore = useNavbarInfo();
navbarStore.changeInfo("Numbers", [
  { title: "My Numbers", link: "/numbers" },
  { title: "Registrations", link: "/number-registrations" },
]);

onMounted(async () => {
  isLoadingRef.value = true;
  try {
    const [reserved, assigned] = await Promise.all([
      getReservedBpNumbers(),
      getBpNumbersList(),
    ]);
    for (const number of reserved) {
      const group = await getNumberGroup(number.groupId);
      number.country = group.country;
      number.needsRegistration = group.meta.needsRegistration;
    }
    reservedNumbers.value = reserved;
    assignedNumbers.value = assigned ?? [];
  } catch (error) {
    console.error("Failed to load numbers:", error);
  } finally {
    isLoadingRef.value = false;
  }
});

const confirmCancelReservation = (number: ReservedNumber) => {
  numberToCancel.value = number;
  showCancelConfirmationModal.value = true;
};

/**
 * Cancels the reservation of the selected number
 */
const handleCancelReservation = async () => {
  if (numberToCancel.value) {
    try {
      const result = await unReserveBpNumber(numberToCancel.value.did);
      reservedNumbers.value = reservedNumbers.value.filter((number) => number.did !== result.did);
      showCancelConfirmationModal.value = false;
      numberToCancel.value = null;
    } catch (error) {
      console.error(error);
    }
  }
};

/**
 * Fetches the number registration list for the given country and opens the modal
 * 
 * @param number The reserved number for which the registration is required
 */
const fetchAndOpenModalNumbersRegistration = async (number: ReservedNumber) => {
  isLoadingNumberRegistrations.value = true;
  countryIsoOfSelectedNumber.value = number.country.iso;
  showSelectRegistrationModal.value = true;
  try {
    const numregs = await getNumberRegistrationListByCountry(number.country.iso);
    selectedReservedNumberDid.value = number.did; // set the selected reserved number
    numberRegistrations.value = numregs; // set the number registration list
    isLoadingNumberRegistrations.value = false;
  } catch (error) {
    console.error(error);
  }
};

/**
 * Assigns the selected number registration to the selected reserved number
 */
const handleAssignRegistration = async () => {
  if (!selected.value) {
    console.log("Select a registration");
    return;
  }
  // set the number registration id
  reservedNumbers.value = reservedNumbers.value.map((number) => {
    if (number.did === selectedReservedNumberDid.value) {
      number.numberRegistrationId = selected.value;
    }
    return number;
  });
  selectedNumberRegistration.value = numberRegistrations.value.find((reg) => reg._id === selected.value) as NumberRegistration;
  showSelectRegistrationModal.value = false;
}

/**
 * Removes the registration from the selected reserved number
 */
const removeRegistrationFromNumber = async () => {
  reservedNumbers.value = reservedNumbers.value.map((number) => {
    if (number.did === selectedReservedNumberDid.value) {
      number.numberRegistrationId = undefined;
    }
    return number;
  });
  selectedNumberRegistration.value = undefined;
};

/**
 * Subscribes to the selected reserved number
 */
const subscribeToNumber = async () => {
  if (!selectedNumberRegistration.value) {
    console.log("Select a registration");
    return;
  }
  isLoadingOrder.value = true;
  let hasError = false
  const selectedReservedNumberId = reservedNumbers.value.find((number) => number.did === selectedReservedNumberDid.value)?._id;
  if (!selectedReservedNumberId) {
    console.error("Selected reserved number not found");
    return;
  }
  try {
    const order = await startBeesphoneSoloWithNumberSubscription(
      selectedReservedNumberId, 
      selectedNumberRegistration.value._id!
    );
    orderId.value = order.orderId;
  } catch (error) {
    hasError = true;
    console.error(`Error creating order: ${error}`);
  }
  try {
    if (!hasError) {
      const scs = await getStripeCustomerSessionClientSecret();
      customerSessionClientSecret.value = scs.sessionClientSecret;
    }
  } catch (error) {
    hasError = true;
    console.error(`Error getting stripe customer session client secret: ${error}`);
  }
  showStripePricingModal.value = !hasError;
  isLoadingOrder.value = false;
};

/**
 * Opens confirmation modal to leave invited number.
 */
const confirmLeaveInvitedNumber = (number: AssignedNumber) => {
  invitedNumberToLeave.value = number;
  showLeaveConfirmationModal.value = true;
};

/**
 * Shows a lightweight modal that redirects number-management expectations to mobile.
 */
const openManageInAppModal = (number: AssignedNumber) => {
  numberToManage.value = number;
  showManageInAppModal.value = true;
};

/**
 * Creates a stable per-destination identity so Vue row keys, optimistic updates,
 * and mutation targeting all resolve the same destination.
 */
const getDestinationKey = (dest: Destination) => {
  if (dest.agentId) return `ca:${dest.agentId}`;
  if (dest.u?.email) return `bu:${dest.u.email}`;
  if (dest.number) return `pn:${dest.number}`;
  return `unknown:${dest.type ?? 'destination'}:${dest.title}:${String(dest.createdAt ?? '')}`;
};

/**
 * Resolves the identifiers needed by updateBpNumberDestination for a given destination.
 * Only one identifier is returned, using the actual identifier field present on the destination.
 */
const resolveDestinationIdentifier = (dest: Destination) => {
  if (dest.agentId) return { agentId: dest.agentId };
  if (dest.u?.email) return { email: dest.u.email };
  if (dest.number) return { destinationNumber: dest.number };

  throw new Error('Destination is missing an identifier');
};

/**
 * Toggles the isEnabled flag on an individual destination.
 * Applies an optimistic update and reverts on error.
 */
const toggleDestination = async (did: string, dest: Destination) => {
  let identifiers: { destinationNumber?: string; email?: string; agentId?: string };

  try {
    identifiers = resolveDestinationIdentifier(dest);
  } catch (error) {
    console.error('Failed to resolve destination identifier:', error);
    return;
  }

  const newValue = dest.isEnabled !== false ? false : true;
  const targetKey = getDestinationKey(dest);

  // Optimistic update
  const number = assignedNumbers.value.find((n) => n.did === did);
  const localDest = number?.destinations?.find((d) => getDestinationKey(d) === targetKey);
  const previousValue = localDest?.isEnabled;

  if (localDest) {
    localDest.isEnabled = newValue;
  }

  try {
    await toggleDestinationEnabled(did, newValue, identifiers);
  } catch (error) {
    // Revert on failure
    if (localDest) {
      localDest.isEnabled = previousValue;
    }
    console.error('Failed to toggle destination:', error);
  }
};

/**
 * Leaves selected invited number and refreshes in-memory list.
 */
const handleLeaveInvitedNumber = async () => {
  if (!invitedNumberToLeave.value) {
    return;
  }

  try {
    await unlinkInvitedNumber(invitedNumberToLeave.value.did);
    assignedNumbers.value = assignedNumbers.value.filter((number) => number._id !== invitedNumberToLeave.value?._id);
  } catch (error) {
    console.error(error);
  } finally {
    showLeaveConfirmationModal.value = false;
    invitedNumberToLeave.value = null;
  }
};

/**
 * Formats ISO date to readable short date for invited-number joined metadata.
 */
const formatJoinedDate = (isoDate: string) => {
  const value = new Date(isoDate);
  return Number.isNaN(value.getTime()) ? "" : value.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

/**
 * Returns the identity name based on the identity type
 * 
 * @param identityType The type of identity
 * @param identity The identity object
 */
const identityName = (identityType: string, identity: BusinessIdentity | PersonalIdentity) => {
  return identityType === 'business' ? (identity as BusinessIdentity).companyName : (identity as PersonalIdentity).firstName + ' ' + (identity as PersonalIdentity).lastName;
};

/**
 * Opens add-number service picker, matching mobile choose-service flow.
 */
const openChooseServiceSheet = () => {
  selectedServiceType.value = "phone";
  showChooseServiceSheet.value = true;
};

/**
 * Continues to number selection with selected service context.
 */
const continueWithSelectedService = () => {
  showChooseServiceSheet.value = false;
  router.push({
    path: "/select-number",
    query: {
      service: selectedServiceType.value,
    },
  });
};
</script>

<style scoped>
.sheet-fade-enter-active,
.sheet-fade-leave-active {
  transition: opacity 0.2s ease;
}

.sheet-fade-enter-from,
.sheet-fade-leave-to {
  opacity: 0;
}

.sheet-slide-enter-active,
.sheet-slide-leave-active {
  transition: transform 0.25s ease, opacity 0.25s ease;
}

.sheet-slide-enter-from,
.sheet-slide-leave-to {
  transform: translateY(24px);
  opacity: 0;
}
</style>